/**
 * CompetitionModals - Modal Management Component
 *
 * Single responsibility: Rendering all modals for CompetitionView.
 * Separates modal UI from main component logic.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import QuickPicksModal from '@/components/modals/QuickPicksModal';
import PicksUpdatedModal from '@/components/modals/PicksUpdatedModal';
import PickemLeaderboard from '@/components/leaderboard/PickemLeaderboard';

interface CompetitionModalsProps {
  // Modal states
  isQuickPickOpen: boolean;
  isFavPickOpen: boolean;
  isClearOpen: boolean;
  isSuccessOpen: boolean;
  isLeaderboardOpen: boolean;

  // Modal handlers
  onCloseQuickPick: () => void;
  onCloseFavPick: () => void;
  onCloseClear: () => void;
  onCloseSuccess: () => void;
  onCloseLeaderboard: () => void;

  // Action handlers
  onConfirmQuickPick: () => void;
  onConfirmFavPick: () => void;
  onConfirmClear: () => void;
  onReturnToLobby: () => void;

  // Data
  gameId: string;
  currentUserId?: string;
  hasExistingSelections: boolean;
}

/**
 * Component that renders all modals for CompetitionView
 */
export const CompetitionModals: React.FC<CompetitionModalsProps> = ({
  isQuickPickOpen,
  isFavPickOpen,
  isClearOpen,
  isSuccessOpen,
  isLeaderboardOpen,
  onCloseQuickPick,
  onCloseFavPick,
  onCloseClear,
  onCloseSuccess,
  onCloseLeaderboard,
  onConfirmQuickPick,
  onConfirmFavPick,
  onConfirmClear,
  onReturnToLobby,
  gameId,
  currentUserId,
  hasExistingSelections,
}) => {
  const { t } = useTranslation();

  return (
    <>
      {/* Quick Pick Confirmation Modal */}
      <QuickPicksModal
        isOpen={isQuickPickOpen}
        onClose={onCloseQuickPick}
        onConfirm={onConfirmQuickPick}
        title={t('modal.quickPick.title')}
        description={t('modal.quickPick.description')}
        confirmText={t('modal.quickPick.confirm')}
        type="quick"
      />

      {/* Popular Pick Confirmation Modal */}

      <QuickPicksModal
        isOpen={isFavPickOpen}
        onClose={onCloseFavPick}
        onConfirm={onConfirmFavPick}
        title={t('modal.favouritePick.title')}
        description={t('modal.favouritePick.description')}
        confirmText={t('modal.favouritePick.confirm')}
        type="fav"
      />

      {/* Clear Selections Confirmation Modal */}
      <QuickPicksModal
        isOpen={isClearOpen}
        onClose={onCloseClear}
        onConfirm={onConfirmClear}
        title={t('modal.clearPicks.title')}
        description={t('modal.clearPicks.description')}
        confirmText={t('modal.clearPicks.confirm')}
        type="clear"
      />

      {/* Picks Updated Success Modal */}
      <PicksUpdatedModal
        isOpen={isSuccessOpen}
        onClose={onCloseSuccess}
        onReturnToLobby={onReturnToLobby}
        isUpdate={hasExistingSelections}
        // TODO: Calculate actual potential win based on selections
        potentialWin="813.95"
        betAmount="10"
      />

      {/* Leaderboard Modal */}
      <PickemLeaderboard
        pickemId={gameId}
        isOpen={isLeaderboardOpen}
        onClose={onCloseLeaderboard}
        currentUserId={currentUserId}
      />
    </>
  );
};
